"use client";
import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";

// ---------------------------------------------------------------------------
// Pricing constants — TODO: tune to actual Coldfire / competitor pricing
// ---------------------------------------------------------------------------
const COLDFIRE_PRICE_PER_SEAT = 69; // EUR / seat / month — Coldfire CRM seat (incl. AI)
// Coldfire supports BYO SIP. Provider-neutral reference price for a DE landline + mobile
// flat per seat; e.g. sipgate, Plusnet, fonial offer flats in this range.
const SIP_FLAT_PER_SEAT = 10; // EUR / seat / month

// Close.com Growth plan — billing happens per started minute (we ceil seconds → minutes)
const CLOSE_GROWTH_PER_SEAT_USD = 109; // USD / user / month — Growth seat
const CLOSE_PER_NUMBER_USD = 1.5; // USD / phone number / month — Twilio DE local number (1 per agent)
const CLOSE_AI_ASSISTANT_USD = 50; // USD / month — Call Assistant flat fee per billing account, does NOT scale with seats
const CLOSE_TWILIO_PER_MIN_USD = 0.03; // USD / billable minute — Twilio DE outbound, blended landline $0.0283 / mobile $0.042
const CLOSE_AI_NOTE_TAKER_PER_MIN_USD = 0.02; // USD / transcribed minute — Call Assistant usage fee

const USD_TO_EUR = 0.86; // Sept 2026
const WORKING_DAYS_PER_MONTH = 22;
const BILLABLE_MINUTES_PER_CALL = 1; // simplification: every call is billed as one started minute

const formatEUR = (n: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

const formatNumber = (n: number) =>
  new Intl.NumberFormat("de-DE").format(Math.round(n));

export const SipCalculator = () => {
  const t = useTranslations("sipCalc");

  const [agents, setAgents] = useState(5);
  const [callsPerDay, setCallsPerDay] = useState(200);

  const {
    totalMinutes,
    coldfireMonthly,
    coldfireBreakdown,
    closeMonthly,
    closeBreakdown,
    savingsMonthly,
    savingsYearly,
  } = useMemo(() => {
    const totalMinutes =
      agents * callsPerDay * BILLABLE_MINUTES_PER_CALL * WORKING_DAYS_PER_MONTH;

    // Coldfire: CRM seat + SIP flat (BYO provider)
    const coldfireSeatCost = agents * COLDFIRE_PRICE_PER_SEAT;
    const sipFlatCost = agents * SIP_FLAT_PER_SEAT;
    const coldfireMonthly = coldfireSeatCost + sipFlatCost;
    const coldfireBreakdown = {
      seats: coldfireSeatCost,
      sipFlat: sipFlatCost,
    };

    // Close.com Growth — all in USD, then converted
    const seatsCostUSD = agents * CLOSE_GROWTH_PER_SEAT_USD;
    const numbersCostUSD = agents * CLOSE_PER_NUMBER_USD;
    const aiCostUSD = CLOSE_AI_ASSISTANT_USD;
    const twilioCostUSD = totalMinutes * CLOSE_TWILIO_PER_MIN_USD;
    const aiNoteTakerCostUSD = totalMinutes * CLOSE_AI_NOTE_TAKER_PER_MIN_USD;
    const closeTotalUSD =
      seatsCostUSD +
      numbersCostUSD +
      aiCostUSD +
      twilioCostUSD +
      aiNoteTakerCostUSD;
    const closeMonthly = closeTotalUSD * USD_TO_EUR;

    const closeBreakdown = {
      seats: seatsCostUSD * USD_TO_EUR,
      numbers: numbersCostUSD * USD_TO_EUR,
      ai: aiCostUSD * USD_TO_EUR,
      twilio: twilioCostUSD * USD_TO_EUR,
      aiNoteTaker: aiNoteTakerCostUSD * USD_TO_EUR,
    };

    const savingsMonthly = closeMonthly - coldfireMonthly;
    const savingsYearly = savingsMonthly * 12;

    return {
      totalMinutes,
      coldfireMonthly,
      coldfireBreakdown,
      closeMonthly,
      closeBreakdown,
      savingsMonthly,
      savingsYearly,
    };
  }, [agents, callsPerDay]);

  const cheaper = coldfireMonthly < closeMonthly;

  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(239,68,68,0.12), transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 md:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-red-300">
            <CalculatorIcon className="h-3 w-3 text-red-400" />
            <span>{t("eyebrow")}</span>
          </div>
          <h2 className="mt-6 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white">
            {t("heading")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-neutral-400 leading-relaxed">
            {t("subheading")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6 md:p-7">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                {t("inputs_title")}
              </h3>

              <div className="mt-6 space-y-7">
                <Slider
                  label={t("agents_label")}
                  value={agents}
                  setValue={setAgents}
                  min={1}
                  max={50}
                  step={1}
                  display={`${agents} ${agents === 1 ? t("agent_unit") : t("agents_unit")}`}
                />
                <Slider
                  label={t("calls_label")}
                  value={callsPerDay}
                  setValue={setCallsPerDay}
                  min={50}
                  max={400}
                  step={10}
                  display={`${callsPerDay} ${t("calls_unit")}`}
                />
              </div>

              <div className="mt-7 pt-6 border-t border-neutral-800/80 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">{t("volume_label")}</span>
                  <span className="font-mono tabular-nums text-neutral-200">
                    {formatNumber(totalMinutes)} {t("min_unit")} / {t("month")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Coldfire */}
            <div className="relative rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-950/40 via-neutral-900 to-neutral-950 p-6 md:p-7 shadow-[0_0_60px_rgba(239,68,68,0.18)]">
              <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-red-300">
                  Coldfire
                </div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-950/60 border border-emerald-900 px-2 py-0.5 rounded">
                  {t("flat_badge")}
                </div>
              </div>
              <motion.div
                key={coldfireMonthly}
                initial={{ opacity: 0.5, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-4 text-4xl md:text-5xl font-bold tabular-nums bg-clip-text text-transparent bg-gradient-to-br from-red-300 via-red-500 to-red-600"
              >
                {formatEUR(coldfireMonthly)}
              </motion.div>
              <div className="mt-1 text-xs text-neutral-500">
                / {t("month")}
              </div>
              <div className="mt-5 space-y-2 text-xs text-neutral-400">
                <Row label={`${t("seat_cost")} (${agents} × ${formatEUR(COLDFIRE_PRICE_PER_SEAT)})`}>
                  {formatEUR(coldfireBreakdown.seats)}
                </Row>
                <Row label={`${t("sip_flat_cost")} (${agents} × ${formatEUR(SIP_FLAT_PER_SEAT)})`}>
                  {formatEUR(coldfireBreakdown.sipFlat)}
                </Row>
                <Row label={t("minute_cost")}>
                  <span className="text-emerald-400 font-medium">
                    {t("flat_via_sip")}
                  </span>
                </Row>
              </div>
            </div>

            {/* Close.com Growth */}
            <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-6 md:p-7">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                  Close.com Growth
                </div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-400 bg-rose-950/40 border border-rose-900/60 px-2 py-0.5 rounded">
                  {t("per_min_badge")}
                </div>
              </div>
              <motion.div
                key={Math.round(closeMonthly)}
                initial={{ opacity: 0.5, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-4 text-4xl md:text-5xl font-bold tabular-nums text-neutral-300"
              >
                {formatEUR(closeMonthly)}
              </motion.div>
              <div className="mt-1 text-xs text-neutral-500">
                / {t("month")}
              </div>
              <div className="mt-5 space-y-2 text-xs text-neutral-400">
                <Row label={`${t("close_seat_cost")} (${agents} × $${CLOSE_GROWTH_PER_SEAT_USD})`}>
                  {formatEUR(closeBreakdown.seats)}
                </Row>
                <Row label={`${t("numbers_cost")} (${agents} × $${CLOSE_PER_NUMBER_USD.toFixed(2)})`}>
                  {formatEUR(closeBreakdown.numbers)}
                </Row>
                <Row label={`${t("ai_cost")} ($${CLOSE_AI_ASSISTANT_USD} ${t("flat")})`}>
                  {formatEUR(closeBreakdown.ai)}
                </Row>
                <Row label={`${t("twilio_cost")} (${formatNumber(totalMinutes)} × $${CLOSE_TWILIO_PER_MIN_USD})`}>
                  {formatEUR(closeBreakdown.twilio)}
                </Row>
                <Row label={`${t("ai_note_taker_cost")} (${formatNumber(totalMinutes)} × $${CLOSE_AI_NOTE_TAKER_PER_MIN_USD})`}>
                  {formatEUR(closeBreakdown.aiNoteTaker)}
                </Row>
              </div>
            </div>

            {/* Savings */}
            <div className="md:col-span-2 mt-2 rounded-2xl border border-neutral-800 bg-neutral-950 p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-1">
                  {cheaper ? t("savings_label") : t("savings_label_neg")}
                </div>
                <motion.div
                  key={savingsMonthly}
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25 }}
                  className={`text-2xl md:text-3xl font-bold tabular-nums ${
                    cheaper
                      ? "bg-clip-text text-transparent bg-gradient-to-r from-red-300 via-red-500 to-red-600"
                      : "text-neutral-400"
                  }`}
                >
                  {cheaper ? "−" : "+"}
                  {formatEUR(Math.abs(savingsMonthly))} / {t("month")}
                </motion.div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-1">
                  {t("yearly_label")}
                </div>
                <div
                  className={`text-xl md:text-2xl font-semibold tabular-nums ${
                    cheaper ? "text-red-300" : "text-neutral-500"
                  }`}
                >
                  {cheaper ? "−" : "+"}
                  {formatEUR(Math.abs(savingsYearly))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-neutral-600">
          {t("footnote")}
        </p>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Slider sub-component
// ---------------------------------------------------------------------------

const Slider = ({
  label,
  value,
  setValue,
  min,
  max,
  step,
  display,
}: {
  label: string;
  value: number;
  setValue: (v: number) => void;
  min: number;
  max: number;
  step: number;
  display: string;
}) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-neutral-300">{label}</label>
        <span className="text-sm font-mono tabular-nums text-white font-semibold">
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full appearance-none bg-transparent slider-range"
        style={{
          background: `linear-gradient(to right, rgb(239 68 68) 0%, rgb(239 68 68) ${pct}%, rgb(38 38 38) ${pct}%, rgb(38 38 38) 100%)`,
          height: 4,
          borderRadius: 9999,
        }}
      />
      <style jsx>{`
        .slider-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: white;
          border: 2px solid rgb(239 68 68);
          border-radius: 9999px;
          cursor: pointer;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
          transition: box-shadow 0.15s;
        }
        .slider-range::-webkit-slider-thumb:hover {
          box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.25);
        }
        .slider-range::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: white;
          border: 2px solid rgb(239 68 68);
          border-radius: 9999px;
          cursor: pointer;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-neutral-500">{label}</span>
    <span className="text-neutral-300 tabular-nums">{children}</span>
  </div>
);

const CalculatorIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 7h8M8 11h2M12 11h2M16 11h0M8 15h2M12 15h2M16 15h0M8 19h2M12 19h6" />
  </svg>
);
